import React, { useState } from 'react';
import BoardStatusSelector from './BoardStatusSelector';

function DraggableBoardHeader({ 
  board, 
  ticketCount, 
  effortSummary,
  onEdit, 
  onDelete, 
  onStatusChange,
  onDragStart, 
  onDragEnd,
  isDragging,
  isOver 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(board.name);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
    setMenuOpen(false);
  };

  const handleSave = () => {
    if (editName.trim() && editName !== board.name) {
      onEdit(board.id, { name: editName.trim() });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditName(board.name);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleStatusChange = (newStatus) => {
    onStatusChange(board.id, newStatus);
  };

  const getEffortBadgeStyle = () => {
    if (!effortSummary || effortSummary.totalDays === 0) {
      return {
        backgroundColor: '#6c757d',
        color: 'white'
      };
    }
    
    const { totalDays } = effortSummary;
    
    // Color based on total effort
    if (totalDays <= 5) {
      return {
        backgroundColor: '#28a745',
        color: 'white'
      };
    } else if (totalDays <= 15) {
      return {
        backgroundColor: '#ffc107',
        color: '#333'
      };
    } else {
      return {
        backgroundColor: '#dc3545',
        color: 'white'
      };
    }
  };

  return (
    <div 
      className={`board-header-draggable ${isDragging ? 'dragging' : ''} ${isOver ? 'drag-over' : ''}`}
      draggable={!isEditing}
      onDragStart={(e) => onDragStart(e, board)}
      onDragEnd={onDragEnd}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0.75rem 1rem',
        backgroundColor: board.color || '#007bff',
        color: 'white',
        cursor: isEditing ? 'default' : 'grab',
        borderRadius: '8px 8px 0 0',
        position: 'relative',
        borderLeft: isOver ? '4px solid #28a745' : '4px solid transparent',
        transition: 'all 0.2s ease',
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        gap: '8px'
      }}
    >
      {/* First Row: Board Name + Status + Menu */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        width: '100%'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyPress}
              autoFocus
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '4px',
                padding: '4px 8px',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 'bold',
                width: '100%'
              }}
            />
          ) : (
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold' }}>
              {board.name}
            </h3>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Status Selector */}
          <BoardStatusSelector
            currentStatus={board.status || 'new'}
            onStatusChange={handleStatusChange}
            disabled={isEditing}
          />

          {/* Menu Button */}
          {!isEditing && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ⋮
              </button>
              
              {menuOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  minWidth: '150px'
                }}>
                  <button
                    onClick={handleEdit}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: '#333'
                    }}
                  >
                    ✏️ Edit Name
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete "${board.name}"? All tickets will be moved to Backlog.`)) {
                        onDelete(board.id);
                      }
                      setMenuOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: '#dc3545'
                    }}
                  >
                    🗑️ Delete Board
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Second Row: Ticket Count + Effort Summary */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        justifyContent: 'flex-start'
      }}>
        {/* Ticket Count Badge */}
        <span 
          className="task-count-badge"
          style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            padding: '4px 10px',
            fontSize: '0.8rem',
            borderRadius: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          🎫 {ticketCount} {ticketCount === 1 ? 'ticket' : 'tickets'}
        </span>

        {/* Effort Summary Badge */}
        {effortSummary && effortSummary.totalDays > 0 && (
          <span 
            style={{
              ...getEffortBadgeStyle(),
              padding: '4px 10px',
              fontSize: '0.8rem',
              borderRadius: '12px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title={`Total: ${effortSummary.totalDays} days | Average: ${effortSummary.avgDays.toFixed(1)} days per ticket`}
          >
            ⚡ {effortSummary.totalDays} {effortSummary.totalDays === 1 ? 'day' : 'days'}
          </span>
        )}

        {/* Show "No estimates" badge if no effort data */}
        {effortSummary && effortSummary.totalDays === 0 && ticketCount > 0 && (
          <span 
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.8)',
              padding: '4px 10px',
              fontSize: '0.75rem',
              borderRadius: '12px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontStyle: 'italic'
            }}
            title="No effort estimations provided for tickets in this board"
          >
            📝 No estimates
          </span>
        )}
      </div>

      {/* Click outside handler */}
      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
}

export default DraggableBoardHeader;